import { FC, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { SectionTitle } from 'src/components/SectionTitle'
import { Skeleton } from 'src/components/Skeleton'
import { useGetConversationCategorizationQuery } from 'src/reactQuery/useGetConversationCategorizationQuery'
import { kFormatter } from 'src/utils/math'
import classes from './conversationCategories.module.scss'

type ViewMode = 'platform' | 'operator'

interface ConversationCategoriesProps {
  fromDate?: string
  pageId: string
  toDate?: string
}

interface CategoryItem {
  conversations: Record<string, number>
  id: string
  name: string
  totalConversations: number
}

interface Series {
  color: string
  key: string
  label: string
}

interface HoveredGroup {
  category: CategoryItem
  x: number
  y: number
}

// Fixed platform series + colors, matching the platform palette used across
// the inbox widgets (Message Volume Trends).
const PLATFORM_SERIES: Series[] = [
  { color: '#8080FF', key: 'facebook', label: 'Facebook' },
  { color: '#F0625D', key: 'instagram', label: 'Instagram' },
  { color: '#54B689', key: 'whatsapp', label: 'WhatsApp' },
  { color: '#0A4FA3', key: 'booking', label: 'Booking' },
  { color: '#F5D328', key: 'expedia', label: 'Expedia' },
]

// Cycled through for the operator view, where the series count is dynamic.
const OPERATOR_PALETTE = [
  '#8080FF',
  '#F0625D',
  '#54B689',
  '#F2A23B',
  '#5AA9E6',
  '#B57BE0',
  '#E86BA5',
  '#3FBFB0',
]

const MAX_VISIBLE_CATEGORIES = 6
const MAX_BAR_HEIGHT = 260
const Y_AXIS_STEPS = 5

// From conversationCategories.module.scss: .statValue is 20px/120% (24px tall)
// and .chart is a fixed 360px. The chart skeleton takes that exact height so
// the chart region reserves its final footprint before the bars land.
const STAT_VALUE_HEIGHT = 24
const CHART_HEIGHT = 360
const CHART_RADIUS = 12

const csvCell = (value: string) => {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`
  return value
}

export const ConversationCategories: FC<ConversationCategoriesProps> = ({
  fromDate,
  pageId,
  toDate,
}) => {
  const [mode, setMode] = useState<ViewMode>('platform')
  const [hovered, setHovered] = useState<HoveredGroup | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const { data, isLoading } = useGetConversationCategorizationQuery({
    fromDate,
    pageId,
    toDate,
    type: mode,
  })

  // First load only. Switching the Platforms/Operators tab refetches, and this
  // keeps the previous values on screen rather than replacing the whole widget.
  // It also has to gate the empty state: `allCategories` is [] before the
  // response lands, which is indistinguishable from genuinely having none.
  const isInitialLoading = isLoading && !data

  const allCategories: CategoryItem[] = useMemo(
    () => (data?.data as CategoryItem[] | undefined) ?? [],
    [data]
  )

  // Series for the active view: platforms that actually have data on this
  // page (an OTA-only page shouldn't list FB/IG/WA in the legend), or the
  // operator roster.
  const series: Series[] = useMemo(() => {
    if (mode === 'platform') {
      const withData = PLATFORM_SERIES.filter((platform) =>
        allCategories.some(
          (category) => (category.conversations?.[platform.key] ?? 0) > 0
        )
      )
      return withData.length ? withData : PLATFORM_SERIES
    }
    const operators = (data?.operators as { id: string; name: string }[]) ?? []
    return operators.map((operator, index) => ({
      color: OPERATOR_PALETTE[index % OPERATOR_PALETTE.length],
      key: operator.id,
      label: operator.name,
    }))
  }, [allCategories, data, mode])

  const categories = useMemo(
    () => allCategories.slice(0, MAX_VISIBLE_CATEGORIES),
    [allCategories]
  )

  const stats = data?.stats as
    | {
        leastUsedTag?: { name: string } | null
        mostUsedTag?: { name: string } | null
      }
    | undefined

  // Chart scale from the largest single series value on screen.
  const maxValue = useMemo(() => {
    let max = 0
    categories.forEach((category) => {
      series.forEach((s) => {
        max = Math.max(max, category.conversations?.[s.key] ?? 0)
      })
    })
    return max
  }, [categories, series])

  const chartMax = Math.max(Math.ceil(maxValue * 1.2), 10)
  const yAxisLabels = useMemo(() => {
    const step = Math.ceil(chartMax / Y_AXIS_STEPS)
    return Array.from(
      { length: Y_AXIS_STEPS + 1 },
      (_, i) => step * i
    ).reverse()
  }, [chartMax])

  const handleDownload = () => {
    if (!allCategories.length) return
    setIsDownloading(true)
    try {
      const header = ['Category', ...series.map((s) => s.label), 'Total Chats']
      const rows = allCategories.map((category) => [
        category.name,
        ...series.map((s) => String(category.conversations?.[s.key] ?? 0)),
        String(category.totalConversations),
      ])
      const csv = [header, ...rows]
        .map((row) => row.map(csvCell).join(','))
        .join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.download = 'conversation-categories.csv'
      link.href = url
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <div className={classes.container}>
      <SectionTitle
        endItem={
          <div className={classes.tabsContainer}>
            <button
              className={`${classes.tab} ${
                mode === 'platform' ? classes.active : ''
              }`}
              onClick={() => setMode('platform')}
              type="button"
            >
              Platforms
            </button>
            <button
              className={`${classes.tab} ${
                mode === 'operator' ? classes.active : ''
              }`}
              onClick={() => setMode('operator')}
              type="button"
            >
              Operators
            </button>
          </div>
        }
        title="Conversation Categories"
        tooltipContent="Categories are auto-generated from message analysis across your conversations."
      />

      <div className={classes.infoRow}>
        <InfoIcon className={classes.infoIcon} />
        <span className={classes.infoText}>
          Categories are auto-generated from message analysis. You&apos;re
          viewing the top {MAX_VISIBLE_CATEGORIES} - download report for full
          list.{' '}
          {isDownloading ? (
            <span className={classes.downloadingText}>Downloading...</span>
          ) : (
            <button
              className={classes.downloadLink}
              onClick={handleDownload}
              type="button"
            >
              Download full report
            </button>
          )}
        </span>
      </div>

      <div className={classes.statsRow}>
        <div className={classes.statCard}>
          <div className={classes.statIcon}>
            <AnnouncementIcon />
          </div>
          <div className={classes.statContent}>
            <div className={classes.statLabel}>Total Categories</div>
            <div className={classes.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={48} />
              ) : (
                allCategories.length.toLocaleString()
              )}
            </div>
          </div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statIcon}>
            <ArrowUpIcon />
          </div>
          <div className={classes.statContent}>
            <div className={classes.statLabel}>Top Category</div>
            <div className={classes.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={120} />
              ) : (
                stats?.mostUsedTag?.name || 'N/A'
              )}
            </div>
          </div>
        </div>

        <div className={classes.statCard}>
          <div className={classes.statIcon}>
            <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
          </div>
          <div className={classes.statContent}>
            <div className={classes.statLabel}>Lowest Category</div>
            <div className={classes.statValue}>
              {isInitialLoading ? (
                <Skeleton height={STAT_VALUE_HEIGHT} width={120} />
              ) : (
                stats?.leastUsedTag?.name || 'N/A'
              )}
            </div>
          </div>
        </div>
      </div>

      {isInitialLoading ? (
        <div className={classes.chartContainer}>
          <Skeleton height={CHART_HEIGHT} radius={CHART_RADIUS} width="100%" />

          {/* The real legend renders below the chart, so the placeholder needs
              a matching row or the chart would still jump when data lands. */}
          <div className={classes.legendContainer}>
            {PLATFORM_SERIES.map((s) => (
              <div className={classes.legendItem} key={s.key}>
                <Skeleton circle width={12} />
                <Skeleton height={13} width={64} />
              </div>
            ))}
          </div>
        </div>
      ) : !categories.length || !series.length ? (
        <div className={classes.emptyState}>
          No category data available for the selected view yet.
        </div>
      ) : (
        <div className={classes.chartContainer}>
          <div className={classes.chart}>
            <div className={classes.yAxis}>
              {yAxisLabels.map((label) => (
                <div className={classes.yLabel} key={label}>
                  {kFormatter(label)}
                </div>
              ))}
            </div>

            <div className={classes.chartArea}>
              <div className={classes.gridLines}>
                {yAxisLabels.map((label) => (
                  <div className={classes.gridLine} key={label} />
                ))}
              </div>

              <div className={classes.bars}>
                {categories.map((category) => (
                  <div className={classes.barGroup} key={category.id}>
                    <div
                      className={classes.groupBars}
                      onMouseLeave={() => setHovered(null)}
                      onMouseMove={(event) => {
                        setHovered({
                          category,
                          x: event.clientX,
                          y: event.clientY,
                        })
                      }}
                    >
                      {series.map((s) => {
                        const value = category.conversations?.[s.key] ?? 0
                        const height =
                          value > 0
                            ? Math.max((value / chartMax) * MAX_BAR_HEIGHT, 4)
                            : 0
                        return (
                          <div className={classes.barCol} key={s.key}>
                            {value > 0 ? (
                              <span className={classes.barValue}>
                                {kFormatter(value)}
                              </span>
                            ) : null}
                            <div
                              className={classes.bar}
                              style={{
                                background: s.color,
                                height: `${height}px`,
                              }}
                            />
                          </div>
                        )
                      })}
                    </div>
                    <div className={classes.groupLabel}>
                      <span className={classes.categoryName}>
                        {category.name}
                      </span>
                      <span className={classes.categoryTotal}>
                        Total Chats:{' '}
                        {category.totalConversations.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={classes.yAxisRight}>
              {yAxisLabels.map((label) => (
                <div className={classes.yLabel} key={label}>
                  {kFormatter(label)}
                </div>
              ))}
            </div>
          </div>

          {hovered &&
            createPortal(
              <div
                className={classes.barTooltip}
                style={{ left: hovered.x, top: hovered.y }}
              >
                <div className={classes.tooltipTitle}>
                  {hovered.category.name}
                </div>
                {series.map((s) => (
                  <div className={classes.tooltipRow} key={s.key}>
                    <span className={classes.tooltipLabel}>
                      <span
                        className={classes.tooltipDot}
                        style={{ backgroundColor: s.color }}
                      />
                      {s.label}
                    </span>
                    <span className={classes.tooltipValue}>
                      {(
                        hovered.category.conversations?.[s.key] ?? 0
                      ).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>,
              document.body
            )}

          <div className={classes.legendContainer}>
            {series.map((s) => (
              <div className={classes.legendItem} key={s.key}>
                <div
                  className={classes.legendColor}
                  style={{ backgroundColor: s.color }}
                />
                <span className={classes.legendLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
